/** The microphone, as an SVG that takes the current color. */
export const micIcon = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z"/>
  </svg>
`

const template = document.createElement('template')
template.innerHTML = `
  <style>
    :host {
      display: inline-block;
      width: var(--control-height);
      height: var(--control-height);
    }
    :host([hidden]) {
      display: none;
    }
    button {
      display: grid;
      place-items: center;
      box-sizing: border-box;
      width: 100%;
      height: 100%;
      padding: 0;
      border: var(--border-width) solid var(--color-border);
      border-radius: var(--voice-radius, var(--radius-sm));
      background: var(--color-input);
      color: var(--color-text-secondary);
      cursor: pointer;
      /* Holding it down shouldn't select text or open a menu. */
      user-select: none;
      -webkit-user-select: none;
      -webkit-touch-callout: none;
      touch-action: none;
    }
    button:hover {
      border-color: var(--color-accent);
    }
    svg {
      width: 50%;
      height: 50%;
      fill: currentColor;
    }
    button[data-state="listening"] {
      border-color: var(--color-accent-strong);
      background: var(--color-accent-strong);
      color: var(--color-text-on-accent);
      animation: pulse 1.2s ease-in-out infinite;
    }
    button[data-state="transcribing"] {
      color: var(--color-accent-strong);
      animation: pulse 1.2s ease-in-out infinite;
    }
    /* The accent button is orange to begin with, so it darkens while listening. */
    :host([accent]) button {
      border-color: var(--color-accent-strong);
      background: var(--color-accent-strong);
      color: var(--color-text-on-accent);
    }
    :host([accent]) button:is(:hover, [data-state="listening"]) {
      border-color: var(--color-accent);
      background: var(--color-accent);
    }
    @keyframes pulse {
      50% { opacity: 0.6; }
    }
  </style>
  <button type="button" title="Tap or hold to talk" aria-label="Tap or hold to talk">${micIcon}</button>
`

/** How long a voice prompt's message stays up. */
const messageTimeout = 4000
/** How long the button has to be held to talk only while it's held. */
const holdTimeout = 1000

/**
 * A button to prompt an agent by voice. Pressing it fires `voice-start`, and
 * `voice-stop` follows when it's let go after a long hold, or pressed again
 * after a tap. The server transcribes what the mic picked up in between and
 * sends it; `voiceResult()` reports how that went.
 *
 * With the `accent` attribute it's filled with the accent color, and the
 * `--voice-radius` property rounds its corners.
 *
 * It doesn't show any text itself. Whoever holds it shows its `hint` while
 * the mic is on (`listening`) and its `message` once a prompt is done, both of which can
 * change whenever it fires `voice-change`.
 */
export class AgentVoice extends HTMLElement {
  #root: ShadowRoot
  #button: HTMLButtonElement
  #message = ''
  #messageTimer?: number
  // Whether the mic is on, and how many voice prompts the server hasn't
  // reported on yet. It reports on each once.
  #listening = false
  #pendingVoice = 0
  // Whether the press that turned the mic on is still down, and whether it's
  // been down long enough that letting go ends the prompt.
  #pressed = false
  #holding = false
  #holdTimer?: number

  constructor() {
    super()
    this.#root = this.attachShadow({ mode: 'open' })
    this.#root.append(template.content.cloneNode(true))
    const button = this.#button = this.#root.querySelector('button')!

    // Capturing the pointer means letting go anywhere counts.
    button.addEventListener('pointerdown', e => {
      if (e.button !== 0) return
      e.preventDefault()
      button.setPointerCapture(e.pointerId)
      this.#press()
    })
    button.addEventListener('lostpointercapture', () => this.#release())
    button.addEventListener('contextmenu', e => e.preventDefault())
    // Space or Enter presses it from the keyboard.
    button.addEventListener('keydown', e => {
      if (e.key !== ' ' && e.key !== 'Enter') return
      e.preventDefault()
      if (!e.repeat) this.#press()
    })
    button.addEventListener('keyup', e => {
      if (e.key === ' ' || e.key === 'Enter') this.#release()
    })
    button.addEventListener('blur', () => this.#release())
  }

  /** Whether the mic is on. */
  get listening() {
    return this.#listening
  }

  /** What to tell the user while the mic is on, or empty when it's off. */
  get hint() {
    return !this.#listening ? ''
      : this.#holding ? 'Listening… let go to send'
      : this.#pressed ? 'Listening…'
      : 'Listening… press the mic again to send'
  }

  /** What came of the last voice prompt, if it didn't go as planned. It clears itself after a while. */
  get message() {
    return this.#message
  }

  /**
   * Reports what came of a voice prompt: the text sent to the agent, which is
   * empty if nothing was heard, or why it failed.
   */
  voiceResult(text: string, error?: string) {
    this.#pendingVoice = Math.max(0, this.#pendingVoice - 1)
    // With none left, the one in progress has ended too, such as for being too long.
    if (this.#pendingVoice === 0) {
      this.#listening = false
      this.#endPress()
    }
    if (error) this.#setMessage(`Voice prompt failed: ${error}`)
    else if (text === '') this.#setMessage("Didn't catch that. Try talking closer to the mic.")
    this.#update()
  }

  // Pressing the button turns the mic on, or off if a tap left it on.
  #press() {
    if (this.#listening) {
      if (!this.#pressed) this.#stop()
      return
    }
    this.#start()
    this.#pressed = true
    this.#holdTimer = setTimeout(() => {
      this.#holding = true
      this.#update()
    }, holdTimeout)
  }

  // Letting go after a long hold ends the prompt, and after a tap leaves the
  // mic on until the next press.
  #release() {
    if (!this.#pressed) return
    const holding = this.#holding
    this.#endPress()
    if (holding) this.#stop()
    else this.#update()
  }

  #endPress() {
    clearTimeout(this.#holdTimer)
    this.#pressed = false
    this.#holding = false
  }

  #start() {
    this.#listening = true
    this.#pendingVoice++
    this.#setMessage('')
    this.#update()
    this.dispatchEvent(new CustomEvent('voice-start', { bubbles: true, composed: true }))
  }

  #stop() {
    this.#listening = false
    this.#update()
    this.dispatchEvent(new CustomEvent('voice-stop', { bubbles: true, composed: true }))
  }

  #setMessage(text: string) {
    clearTimeout(this.#messageTimer)
    this.#message = text
    if (text) {
      this.#messageTimer = setTimeout(() => {
        this.#message = ''
        this.#update()
      }, messageTimeout)
    }
  }

  #update() {
    const button = this.#button
    if (this.#listening) button.dataset.state = 'listening'
    else if (this.#pendingVoice > 0) button.dataset.state = 'transcribing'
    else delete button.dataset.state
    this.dispatchEvent(new CustomEvent('voice-change'))
  }
}

customElements.define('meat-agent-voice', AgentVoice)

declare global {
  interface HTMLElementTagNameMap {
    'meat-agent-voice': AgentVoice
  }
  interface HTMLElementEventMap {
    'voice-start': CustomEvent<void>
    'voice-stop': CustomEvent<void>
    'voice-change': CustomEvent<void>
  }
}
